import React, { useEffect, useState } from 'react';
import { Smartphone, X, Share } from 'lucide-react';

// "Add to Home Screen" prompt. Two flavours:
//   • Android / Chromium: catches the `beforeinstallprompt` event and shows a
//     one-tap Install button.
//   • iOS Safari: no event exists, so we detect Safari-on-iOS heuristically and
//     show the Share → Add to Home Screen instructions instead.
// Dismissal is remembered in localStorage so we don't pester the user.
const DISMISS_KEY = 'rm_install_dismissed_at';
const REPROMPT_DAYS = 14;

const isStandalone = () => (
  window.matchMedia?.('(display-mode: standalone)').matches ||
  // iOS Safari
  window.navigator?.standalone === true
);

const isIosSafari = () => {
  const ua = window.navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  return isIOS && isSafari;
};

const wasRecentlyDismissed = () => {
  const t = localStorage.getItem(DISMISS_KEY);
  if (!t) return false;
  const days = (Date.now() - parseInt(t, 10)) / 86400000;
  return days < REPROMPT_DAYS;
};

const InstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);  // Android beforeinstallprompt event
  // Lazy-init so the iOS-Safari check happens once at mount and the lint rule
  // doesn't complain about a setState-in-effect.
  const [showIos] = useState(() => isIosSafari() && !isStandalone() && !wasRecentlyDismissed());
  const [open, setOpen] = useState(() => !isStandalone() && !wasRecentlyDismissed());

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
    setOpen(false);
  };

  const handleInstall = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch {
      /* user cancelled — ignore */
    }
    setDeferred(null);
    dismiss();
  };

  if (!open) return null;
  if (!deferred && !showIos) return null;

  return (
    <div
      className="bg-brand-accent/5 border border-brand-accent/20 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6 flex items-start gap-3"
      data-testid="install-prompt"
    >
      <div className="shrink-0 w-9 h-9 rounded-lg bg-brand-accent/10 flex items-center justify-center mt-0.5">
        <Smartphone className="w-4 h-4 text-brand-accent" />
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-brand-ink">Add ReflectiveMind to your home screen</p>

        {deferred && (
          <p className="text-xs text-brand-ink-muted mt-0.5">
            One tap and we&apos;re always a thumb away — no app store needed.
          </p>
        )}

        {showIos && !deferred && (
          <p className="text-xs text-brand-ink-muted mt-0.5 leading-relaxed">
            In Safari, tap the <Share className="inline w-3.5 h-3.5 align-text-bottom mx-0.5" /> Share icon, then choose
            <strong className="text-brand-ink"> &ldquo;Add to Home Screen&rdquo;</strong>. It then opens like an app.
          </p>
        )}

        {deferred && (
          <div className="mt-2.5">
            <button
              onClick={handleInstall}
              className="text-xs font-semibold px-3 py-1.5 rounded-md bg-brand-accent hover:bg-brand-accent-hover text-white"
              data-testid="install-prompt-install"
            >
              Install
            </button>
          </div>
        )}
      </div>

      <button
        onClick={dismiss}
        className="shrink-0 text-gray-400 hover:text-gray-600"
        title="Dismiss"
        data-testid="install-prompt-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default InstallPrompt;
